package co.maytes.standupbot.dashboard.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import co.maytes.standupbot.dashboard.styles.ControlClass
import co.maytes.standupbot.dashboard.styles.FontWeight
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Space
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.design.StatusHue
import co.maytes.standupbot.shared.ui.FieldAria
import co.maytes.standupbot.shared.ui.FieldIds
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

class FieldControlProps internal constructor(
    val ids: FieldIds,
    val describedBy: String?,
    val invalid: Boolean,
    val required: Boolean,
    val labelled: Boolean,
) {
    val id: String get() = ids.control
}

fun AttrsScope<*>.fieldControl(props: FieldControlProps) {
    attr("id", props.id)
    props.describedBy?.let { attr("aria-describedby", it) }
    if (props.invalid) attr("aria-invalid", "true")
    if (props.required) attr("aria-required", "true")
    if (props.labelled) attr("aria-labelledby", props.ids.label)
}

fun Modifier.fieldControlAttrs(props: FieldControlProps): Modifier = attrsModifier { fieldControl(props) }

private var fieldSequence = 0

@Composable
fun rememberFieldIds(): FieldIds = remember { FieldAria.ids(FieldAria.baseId(fieldSequence++)) }

@Composable
fun FormField(
    label: String?,
    modifier: Modifier = Modifier,
    hint: String? = null,
    help: String? = null,
    error: String? = null,
    required: Boolean = false,
    reserveMessageSpace: Boolean = true,
    group: Boolean = false,
    ids: FieldIds = rememberFieldIds(),
    control: @Composable (FieldControlProps) -> Unit,
) {
    val props = FieldControlProps(
        ids = ids,
        describedBy = FieldAria.describedBy(ids, hasHint = hint != null, hasHelp = help != null, hasError = error != null),
        invalid = error != null,
        required = required,
        labelled = label != null && group,
    )
    Div(
        attrs = Modifier
            .styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "${Space.s1}")
                property("width", "100%")
                property("min-width", "0")
            }
            .then(modifier)
            .toAttrs(),
    ) {
        if (label != null || hint != null) {
            Div(
                attrs = Modifier
                    .styleModifier {
                        property("display", "flex")
                        property("flex-wrap", "wrap")
                        property("align-items", "baseline")
                        property("column-gap", "${Space.s2}")
                    }
                    .toAttrs(),
            ) {
                if (label != null) {
                    FieldLabelText(
                        text = label,
                        forId = ids.control,
                        required = required,
                        labelId = ids.label,
                        asGroup = group,
                    )
                }
                if (hint != null) FieldHintText(text = hint, id = ids.hint)
            }
        }
        control(props)
        FieldMessageBlock(help = help, error = error, ids = ids, reserve = reserveMessageSpace)
    }
}

@Composable
internal fun FieldLabelText(
    text: String,
    forId: String?,
    required: Boolean,
    labelId: String? = null,
    asGroup: Boolean = false,
) {
    val look = Modifier
        .fontSize(Typography.label.size)
        .lineHeight(Typography.label.lineHeight)
        .fontWeight(FontWeight.medium)
        .color(Palette.textPrimary)
    val content: @Composable () -> Unit = {
        Text(text)
        if (required) {
            Span(
                attrs = Modifier
                    .color(Palette.status(StatusHue.Danger).onSubtle)
                    .styleModifier { property("margin-left", "${Space.s1}") }
                    .toAttrs { attr("aria-hidden", "true") },
            ) { Text("*") }
        }
    }
    if (asGroup || forId == null) {
        Span(
            attrs = look.toAttrs { if (labelId != null) attr("id", labelId) },
        ) { content() }
    } else {
        Label(
            forId = forId,
            attrs = look.toAttrs { if (labelId != null) attr("id", labelId) },
        ) { content() }
    }
}

@Composable
internal fun FieldHintText(text: String, id: String?) {
    Span(
        attrs = Modifier
            .fontSize(Typography.caption.size)
            .lineHeight(Typography.caption.lineHeight)
            .color(Palette.textTertiary)
            .toAttrs { if (id != null) attr("id", id) },
    ) { Text(text) }
}

private val reservedMessageHeight: String =
    "${Typography.caption.size.value * Typography.caption.lineHeight}px"

@Composable
internal fun FieldMessageBlock(help: String?, error: String?, ids: FieldIds?, reserve: Boolean) {
    if (help == null && error == null && !reserve) return
    Div(
        attrs = Modifier
            .classNames(ControlClass.FIELD_MESSAGE)
            .styleModifier {
                property("display", "flex")
                property("align-items", "flex-start")
                property("gap", "${Space.s1}")
                if (reserve) property("min-height", reservedMessageHeight)
            }
            .toAttrs { attr("aria-live", "polite") },
    ) {
        when {
            error != null -> key(error) { FieldErrorLine(error, ids?.error) }
            help != null -> FieldHelpLine(help, ids?.help)
        }
    }
}

@Composable
private fun FieldErrorLine(text: String, id: String?) {
    Span(
        attrs = Modifier
            .classNames(ControlClass.FIELD_ERROR)
            .fontSize(Typography.caption.size)
            .lineHeight(Typography.caption.lineHeight)
            .fontWeight(FontWeight.medium)
            .color(Palette.status(StatusHue.Danger).onSubtle)
            .toAttrs { if (id != null) attr("id", id) },
    ) { Text(text) }
}

@Composable
private fun FieldHelpLine(text: String, id: String?) {
    Span(
        attrs = Modifier
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "${Space.s1}")
            }
            .toAttrs { if (id != null) attr("id", id) },
    ) {
        Span(
            attrs = Modifier
                .styleModifier {
                    property("width", "${Space.s1}")
                    property("height", "${Space.s1}")
                    property("flex-shrink", "0")
                    property("border-radius", "${Radius.pill}")
                    property("background-color", Palette.accent.toString())
                }
                .toAttrs { attr("aria-hidden", "true") },
        )
        Span(
            attrs = Modifier
                .fontSize(Typography.caption.size)
                .lineHeight(Typography.caption.lineHeight)
                .color(Palette.textTertiary)
                .toAttrs(),
        ) { Text(text) }
    }
}
